import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { first, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';

import { AnalyticsService } from '@app/shared/modules/analytics/services/analytics.service';
import { Car, CarModel } from '@app/shared/modules/evc-api/models/car';
import { CheckboxType } from '@app/shared/IFE/components/checkbox/checkbox.component';
import { EvcApiService } from '@app/shared/modules/evc-api/services/evc-api.service';
import { SelectField, SelectFieldDataSort } from '@app/shared/IFE/components/select/select-data';
import { createProvider, Step } from '@app/shared/IFE/step/step/step';
import { PartnerService } from '../../services/partner/partner.service';
import { PartnerUI } from '@app/shared/modules/evc-api/models/partner-ui.interface';
import { CarValues } from '../../models/constants';

export enum Fields {
  carBrandId = 'carBrand.id',
  carBrandName = 'carBrand.name',
  carBrand = 'carBrand',
  carModel = 'carModel',
  additionalCarModel = 'additionalCarModel',
  options = 'options',
}

@Component({
  selector: 'evc-quote-car-details',
  providers: [createProvider(CarDetailsComponent)],
  templateUrl: './car-details.component.html',
  styleUrls: ['./car-details.component.scss'],
})
export class CarDetailsComponent extends Step implements OnInit, OnDestroy {
  @Output() stepNavigation: EventEmitter<string> = new EventEmitter<string>();

  readonly formName = 'evcQuote';
  readonly stepName: string = 'car-details';

  selectField: SelectField;
  carBrands: Car[];
  carModels: CarModel[];
  summaryVisible = true;
  currentCarIndex = 0;

  readonly fields = Fields;
  readonly checkboxType = CheckboxType;

  readonly hasPreSelection$: Observable<boolean>;

  protected readonly notifier = new Subject<void>();
  protected preselected = '';

  constructor(
    private readonly partnerService: PartnerService,
    private evcApiService: EvcApiService,
    fb: UntypedFormBuilder,
    analyticsService: AnalyticsService
  ) {
    super(fb, analyticsService);
    this.hasPreSelection$ = this.partnerService.partner$.pipe(map((partner) => this.isPartnerSelectionCar(partner)));
  }

  get currentCar() {
    return (this.control.get('cars') as UntypedFormArray).at(this.currentCarIndex);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initCarDetails();
  }

  ngOnDestroy() {
    this.notifier.next();
    this.notifier.complete();
  }

  selectNoValue(): void {
    this.onModelChange([]);
    this.formReady.emit();
  }

  skip() {
    this.removeCarAtIndex(this.currentCarIndex);
    this.summaryVisible = true;
  }

  navigate() {
    if (this.summaryVisible) {
      super.navigate();
    } else {
      this.summaryVisible = true;
      this.onModelChange(this.control.get('cars').value);
      this.stepNavigation.emit();
    }
  }

  writeValue(value: []) {
    if (!value || value.length === 0) {
      this.summaryVisible = false;
      this.addNewCar();
      super.writeValue(value);
      return;
    }

    value.forEach(() => {
      (this.control.get('cars') as UntypedFormArray).push(this.buildCarFormGroup());
    });
    super.writeValue({ cars: value });
  }

  removeCarAtIndex(index: number, save = true): void {
    (this.control.get('cars') as UntypedFormArray).removeAt(index);

    if (save) {
      this.onModelChange(this.control.get('cars').value);
      this.stepNavigation.emit();
    }

    if ((this.control.get('cars') as UntypedFormArray).length === 0) {
      this.control.reset();
      this.addNewCar();
      this.preselectCarBrand(this.carBrands);
      this.summaryVisible = false;
    }

    this.currentCarIndex = (this.control.get('cars') as UntypedFormArray).length - 1;
  }

  addNewCar(): void {
    this.summaryVisible = false;
    (this.control.get('cars') as UntypedFormArray).push(this.buildCarFormGroup());
    this.currentCarIndex = (this.control.get('cars') as UntypedFormArray).length - 1;
    this.currentCar.get('carBrand.id').valueChanges.subscribe((value) => {
      this.currentCar
        .get(Fields.carBrandName)
        .patchValue(this.carBrands.find((car) => car.id === Number(value)).name, { emitEvent: false });
      this.currentCar.get(Fields.carModel).patchValue('', { emitEvent: false });
      this.getCarModels(Number(value));
    });
    this.currentCar.get('carModel').valueChanges.subscribe((value) => {
      this.currentCar.get('carModel').patchValue(value, { emitEvent: false });
      this.currentCar.get(Fields.additionalCarModel).patchValue('', { emitEvent: false });

      // When 'not in the list' is selected, additional car model is required
      const validators = this.currentCar.get(Fields.carModel).value === 'notInTheList' ? [Validators.required] : [];
      this.currentCar.get(Fields.additionalCarModel).setValidators(validators);
      this.currentCar.get(Fields.additionalCarModel).updateValueAndValidity({ emitEvent: false });
    });
  }

  trackById(index, item) {
    return item.id;
  }

  protected valueChanges(values) {
    super.valueChanges(values);
  }

  protected buildForm(): void {
    this.control = this.fb.group({
      cars: this.fb.array([]),
    });
    this.subscribeToValueChanges(false);
  }

  private buildCarFormGroup(): UntypedFormGroup {
    return this.fb.group({
      carBrand: this.fb.group({
        id: ['', [Validators.required]],
        name: ['', [Validators.required]],
      }),
      carModel: ['', [Validators.required]],
      additionalCarModel: ['', []],
      options: ['', []],
    });
  }

  private initCarDetails(): void {
    const detailsInit$: Observable<[Car[], PartnerUI]> = this.evcApiService.getCarBrands().pipe(
      first((value) => value !== null),
      withLatestFrom(this.partnerService.partner$),
      takeUntil(this.notifier)
    );

    detailsInit$.subscribe(([brands, partner]) => {
      const options = {};
      this.carBrands = brands;
      brands.forEach((car) => (options[car.id] = car.name));
      this.selectField = {
        id: 'carBrand',
        label: 'modules.evcQuote.steps.carDetails.fields.carDetails.carBrand.label',
        placeholder: 'modules.evcQuote.steps.carDetails.fields.carDetails.carBrand.placeholder',
        sort: SelectFieldDataSort.valueAscOrder,
        options,
      };
      if (this.isPartnerSelectionCar(partner)) {
        this.preselected = partner.name;
        this.preselectCarBrand(this.carBrands);
      }
    });
  }

  private getCarModels(id: number): void {
    this.evcApiService
      .getCarModels(id)
      .pipe(takeUntil(this.notifier))
      .subscribe((carModels) => {
        this.carModels = carModels;
      });
  }

  private preselectCarBrand(carBrands: Car[]): void {
    // the first added car will have the brand preselected
    const selected = carBrands.find((car) => car.name.toLowerCase() === this.preselected);
    setTimeout(() => {
      const control = (this.control.get('cars') as UntypedFormArray).at(0);
      if (control) {
        control.get(this.fields.carBrand).patchValue({ id: selected.id, name: selected.name });
      }
    });
  }

  private isPartnerSelectionCar(partner: PartnerUI): boolean {
    return partner && (partner.name || '').toLowerCase() === CarValues.volvo;
  }
}
